export function normalizeOutcomeLabel(rawOutcome) {
  const value = String(rawOutcome || '').trim().toUpperCase();
  if (value === 'HOME' || value === 'HOME_WIN' || value === 'HOME WIN') return 'HOME';
  if (value === 'DRAW') return 'DRAW';
  if (value === 'AWAY' || value === 'AWAY_WIN' || value === 'AWAY WIN') return 'AWAY';
  return null;
}

/** Human-readable outcome line for active trade cards */
export function formatActiveTradeOutcomes(outcomeTotals) {
  const entries = Object.entries(outcomeTotals || {})
    .filter(([, amount]) => amount > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  if (entries.length === 1) return entries[0][0];
  return entries.map(([outcome, amount]) => `${outcome} (${amount.toFixed(2)})`).join(', ');
}

/** Map market address (lowercase) → claim badge data for fixture cards */
export function buildClaimableByMarket(claimableMarkets) {
  const claimable = {};
  (claimableMarkets || []).forEach((market) => {
    const key = (market.address || '').toLowerCase();
    if (!key) return;
    claimable[key] = {
      amount: market.claimable,
      userBet: market.userBet || '0',
      homeTeam: market.homeTeam,
      awayTeam: market.awayTeam,
    };
  });
  return claimable;
}

/** Global claims widget list */
export function buildClaimableList(claimableMarkets) {
  return (claimableMarkets || []).map((m) => ({
    address: m.address,
    homeTeam: m.homeTeam,
    awayTeam: m.awayTeam,
    result: m.result,
    type: m.type,
    claimableAmount: m.claimable,
  }));
}

/** Fixture card “you have a position” map */
export function buildPositionsByMarket(trades) {
  const grouped = {};
  (trades || []).forEach((trade) => {
    const marketAddress = (trade.market_address || '').toLowerCase();
    if (!marketAddress) return;
    if (!grouped[marketAddress]) {
      grouped[marketAddress] = {
        totalSize: 0,
        tradesCount: 0,
        outcomeTotals: { HOME: 0, DRAW: 0, AWAY: 0 },
      };
    }
    const tradeSize = parseFloat(trade.trade_size || 0);
    grouped[marketAddress].totalSize += tradeSize;
    grouped[marketAddress].tradesCount += 1;
    const normalizedOutcome = normalizeOutcomeLabel(trade.traded_on);
    if (
      normalizedOutcome &&
      Object.prototype.hasOwnProperty.call(grouped[marketAddress].outcomeTotals, normalizedOutcome)
    ) {
      grouped[marketAddress].outcomeTotals[normalizedOutcome] += tradeSize;
    }
  });
  return grouped;
}

/** Global claims “active trades” (pending) */
export function buildActiveTrades(trades) {
  const pending = (trades || []).filter(
    (trade) => (trade.status || '').toUpperCase() === 'PENDING'
  );
  const grouped = {};
  pending.forEach((trade) => {
    const marketAddress = (trade.market_address || '').toLowerCase();
    if (!marketAddress) return;
    if (!grouped[marketAddress]) {
      grouped[marketAddress] = {
        marketAddress,
        homeTeam: trade.home_team,
        awayTeam: trade.away_team,
        marketState: trade.market_state || 'PENDING',
        totalTradeSize: 0,
        kickoffTime: trade.kickoff_time ? Number(trade.kickoff_time) : null,
        outcomeTotals: { HOME: 0, DRAW: 0, AWAY: 0 },
      };
    }
    const tradeSize = parseFloat(trade.trade_size || 0);
    grouped[marketAddress].totalTradeSize += tradeSize;
    const normalizedOutcome = normalizeOutcomeLabel(trade.traded_on);
    if (
      normalizedOutcome &&
      Object.prototype.hasOwnProperty.call(grouped[marketAddress].outcomeTotals, normalizedOutcome)
    ) {
      grouped[marketAddress].outcomeTotals[normalizedOutcome] += tradeSize;
    }
    if (trade.kickoff_time) {
      const parsedKickoff = Number(trade.kickoff_time);
      if (Number.isFinite(parsedKickoff) && parsedKickoff > 0) {
        const currentKickoff = grouped[marketAddress].kickoffTime;
        if (!currentKickoff || parsedKickoff < currentKickoff) {
          grouped[marketAddress].kickoffTime = parsedKickoff;
        }
      }
    }
  });
  return Object.values(grouped)
    .map((trade) => ({
      ...trade,
      outcomeLabel: formatActiveTradeOutcomes(trade.outcomeTotals),
    }))
    .sort((a, b) => {
    const aKickoff = Number(a.kickoffTime || 0);
    const bKickoff = Number(b.kickoffTime || 0);
    const aValid = Number.isFinite(aKickoff) && aKickoff > 0;
    const bValid = Number.isFinite(bKickoff) && bKickoff > 0;
    if (aValid && bValid) return aKickoff - bKickoff;
    if (aValid) return -1;
    if (bValid) return 1;
    return (a.homeTeam || '').localeCompare(b.homeTeam || '');
  });
}
